import React from 'react';
import './PricingAndPackaging.scss';
import { dangerousGettext, gettext } from '@eventbrite/i18n';
import { Button } from '@eventbrite/eds-button';
import { Icon } from '@eventbrite/eds-icon';
import './PricingAndPackagingComparisonCards.scss';
import { BoltFill, Tickets } from '@eventbrite/eds-iconography';
import { CheckChunkierIcon } from '@eventbrite/marmalade';
import { getHost, getLocale } from './utils';
import { getPricesForTLD } from './pricingAndPackagingUtils';

const getCardDetails = () => {
    const priceMapping = getPricesForTLD();
    return [
        {
            badge: (
                <div className="pay-per-event-badge">
                    {gettext('Pay per event')}
                </div>
            ),
            planName: gettext('Flex'),
            planSubtext: gettext('Pay as you go for full flexibility*'),
            perks: [
                gettext('Pay from your ticket sales for no upfront costs'),
                gettext('Send 250 marketing emails a day'),
                gettext(
                    'Access all-in-one event ticketing and marketing tools',
                ),
            ],
            pricing: [
                {
                    description: gettext('Up to 25 tickets'),
                    price: gettext('FREE'),
                },
                {
                    description: gettext('Up to 100 tickets'),
                    price: gettext('%(price)s per event', {
                        price: priceMapping.flexPlan.liteTier,
                    }),
                },
                {
                    description: gettext('Up to 250 tickets'),
                    price: gettext('%(price)s per event', {
                        price: priceMapping.flexPlan.coreTier,
                    }),
                },
                {
                    description: gettext('Unlimited Tickets'),
                    price: gettext('%(price)s per event', {
                        price: priceMapping.flexPlan.proTier,
                    }),
                },
            ],
            footerText: gettext(
                "You won't be charged until you publish an event.",
            ),
        },
        {
            badge: (
                <div className="subscribe-and-save-badge">
                    {gettext('Subscribe and save')}
                    <Icon type={<BoltFill />} color="white" size="xsmall" />
                </div>
            ),
            planName: gettext('Pro'),
            planSubtext: gettext(
                'Save when hosting frequent events.  Try free for 14 days*',
            ),
            perks: [
                gettext('Host unlimited events with your monthly subscription'),
                gettext('Send up to 10,000 marketing emails a day'),
                gettext(
                    'Access all-in-one event ticketing and marketing tools',
                ),
            ],
            pricing: [
                {
                    description: gettext('Up to 100 tickets'),
                    price: gettext('%(price)s per month', {
                        price: priceMapping.proPlan.liteTier,
                    }),
                },
                {
                    description: gettext('Up to 250 tickets'),
                    price: gettext('%(price)s per month', {
                        price: priceMapping.proPlan.coreTier,
                    }),
                },
                {
                    description: gettext('Unlimited Tickets'),
                    price: gettext('%(price)s per month', {
                        price: priceMapping.proPlan.proTier,
                    }),
                },
            ],
            footerText: '',
        },
    ];
};

export const PricingAndPackagingComparisonCards = () => {
    const cardDetails = getCardDetails();
    const locale = Intl.NumberFormat().resolvedOptions().locale;
    return (
        <div className="pricing-and-packaging-comparison-cards">
            <h2 className="cards-header">
                {gettext('Flexible plans that grow with you')}
            </h2>
            <p className="cards-subheader">
                {gettext(
                    'Get started for free, and upgrade your plan anytime!',
                )}
            </p>
            <div className="cards">
                {cardDetails.map((card, i) => {
                    return (
                        <div
                            key={i}
                            className="pricing-and-packaging-comparison-card"
                        >
                            <div className="badge">{card.badge}</div>
                            <div className="plan">
                                <h2>{card.planName}</h2>
                                <h3>{card.planSubtext}</h3>
                            </div>
                            <div className="perks">
                                {card.perks.map((perk, i) => {
                                    return (
                                        <div key={i} className="perk">
                                            <div className="card-icon">
                                                <Icon
                                                    type={<CheckChunkierIcon />}
                                                    strokeColor="vibrant-green"
                                                    color="vibrant-green"
                                                    size="small"
                                                />
                                            </div>
                                            <span>{perk}</span>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="divider" />
                            <h2>{gettext('Pricing')}</h2>
                            <div className="pricing-items">
                                {card.pricing.map((pricingItem, i) => {
                                    return (
                                        <div key={i} className="pricing-item">
                                            <div className="pricing-description">
                                                <div className="card-icon">
                                                    <Icon
                                                        type={<Tickets />}
                                                        strokeColor="ui-blue"
                                                        size="medium"
                                                    />
                                                </div>
                                                <span>
                                                    {pricingItem.description}
                                                </span>
                                            </div>
                                            <span className="pricing-amount">
                                                {pricingItem.price}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="footer-text">{card.footerText}</p>
                            <Button
                                style="fill"
                                onClick={() => {
                                    window.location.href =
                                        '/signin/signup/?referrer=%2Fmanage%2Fevents%2Fcreate%2F';
                                }}
                            >
                                {gettext('Get started for free')}
                            </Button>
                        </div>
                    );
                })}
            </div>
            <p
                className="cards-disclaimer"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: dangerousGettext(
                        '*For all plans, ad spend and <a href="%(ticketingFeesUrl)s" target="__blank">Ticketing Fees</a> may apply. Ticketing Fees are paid by ticket buyers unless you choose to cover them.',
                        {
                            ticketingFeesUrl: `https://${getHost()}/help/${locale.toLowerCase()}/articles/755615`,
                        },
                    ),
                }}
            />
        </div>
    );
};

export default PricingAndPackagingComparisonCards;
